'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import Box from '@mui/material/Box';
import CardContext from '@/contexts/CardContext';
import { textColors } from '@/styles/theme/colors';
import Chip from '@mui/material/Chip';
import DataContext from '@/contexts/DataContext';
import { DataTable } from '@/components/core/data-table';
import { dayjs } from '@/lib/dayjs';
import { fetchExprData, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { icons } from '@/components/dashboard/layout/nav-icons';
import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { logger } from '@/lib/default-logger';
import { useComponentLogger } from '@/lib/component-logger';
import { useInterval } from '@/hooks/use-interval';
import { Link } from 'react-router-dom';

// The first column
// Bolded display name with optional icon or link
const primaryFormatter = (row, index, column) => {
  if (!row[column.field]) return null;
  let rowObj = row[column.field];
  let cellIcon = rowObj.linkValue ? "link" : rowObj.icon;
  const RowIcon = cellIcon && icons[cellIcon] ? icons[cellIcon] : null;
  const iconWeight = cellIcon === 'thermometer-simple' ? 'fill' : 'bold';
  
  let dis = ''; 
  if (rowObj.dis) {
    dis = rowObj.unit !== null && rowObj.unit !== undefined ? rowObj.dis + rowObj.unit : rowObj.dis;
  } else if (rowObj.value) {
    dis = rowObj.unit !== null && rowObj.unit !== undefined ? rowObj.value + rowObj.unit : rowObj.value;
  } 
  logger.debug('primary row', row, column, cellIcon, rowObj, dis);  

  return (
      rowObj.linkValue ?
        <Link to={rowObj.linkValue} style={{textDecoration: 'none', color: 'inherit'}} >
        <Stack direction="row" spacing={1} sx={{  }}>
          <Box sx={{marginRight: -0.5, display: 'flex', alignItems: 'center'}}>
              <RowIcon size={20} weight={iconWeight} />
          </Box>
          <Typography sx={{ typography: { xs: 'subtitle1', sm: 'subtitle1' } }}>
              {dis}
          </Typography>
        </Stack>
        </Link>
      : 
        <Stack direction="row" spacing={1} sx={{ alignItems: {xs: 'center', sm: 'flex-start'}}}>
          {cellIcon ? 
              <Box>
                  <RowIcon size={24} weight={iconWeight}/>
              </Box>
          : <></>} 
          <Typography sx={{ typography: { xs: 'subtitle1', sm: 'subtitle1' } }}>
              {dis}
          </Typography>
        </Stack>
  );
}

// Displays the value as a colored chip
// if no color specified, shows plain text
const statusFormatter = (row, index, column) => {
    
    if (!row[column.field]) return null;
    const rowVal = row[column.field];
    const dis = rowVal.unit ? rowVal.value + rowVal.unit : rowVal.value;
    // logger.debug('status row:', row, index, column, rowVal );

    const mapping = {
      pending: { label: 'yellow', color: 'warning' },
      completed: { label: 'green', color: 'success' },
      canceled: { label: 'red', color: 'error' },
    };
    const { label, color } = mapping[row.status] ?? { label: 'Unknown', color: 'secondary' };

    // return <Chip style={{backgroundColor: 'var(--mui-palette-error-100)', color: 'white'}} label={row[column.field]} size="small" variant="soft" />;
    if (row[column.field].color) {
      return <Chip color={row[column.field].color} label={row[column.field].value} size="small" variant="soft" />;
    } else {
      return (
      <Typography variant='body2' sx={{  }}>
        {dis}
      </Typography>)
    }
} 

// Default formatter
// Renders and optional icon
// If a color is specified, applies color to text and icon
const iconFormatter = (row, index, column) => {
  if (!row[column.field]) return null;
  const rowVal = row[column.field];
  const dis = rowVal.unit ? rowVal.value + rowVal.unit : rowVal.value;
  logger.debug('icon row', row, column, rowVal);

  let cellIcon = row[column.field].icon;
  const RowIcon = cellIcon && icons[cellIcon] ? icons[cellIcon] : null;
  let cellColor = row[column.field].color;
  const textColor = cellColor && textColors[cellColor] ? textColors[cellColor] : textColors['black'];
  const iconWeight = cellIcon === 'thermometer-simple' ? 'fill' : 'bold';

  return (
      // <Stack direction="row" spacing={1} sx={{ alignItems: 'center'}}>
      <Stack direction="row" spacing={1} sx={{ }}>
          {cellIcon ? 
              <Box sx={{marginRight: -0.5}}>
                  <RowIcon size={20} weight={iconWeight} color={textColor}/>
              </Box>
          : <></>} 
          <Typography variant='body2' sx={{  }} color={textColor}>
            {dis}
          </Typography>
      </Stack>
  );
}

const linkFormatter = (row, index, column) => {
  if (!row[column.field]) return null;
  const link = row[column.field]?.linkName ?  row[column.field]?.value : null;
  const linkName = row[column.field]?.linkName ? row[column.field]?.linkName : row[column.field]?.value;
  // const dis = link.unit ? link.value + link.unit : link.value;
  logger.debug('link row', row, column, link);

  // let cellLink = row[column.field].icon;
  const LinkIcon = icons["link"];
  let cellColor = row[column.field].color;
  const textColor = cellColor && textColors[cellColor] ? textColors[cellColor] : textColors['black'];


  return (
    link ?
      <Link to={link} style={{textDecoration: 'none', color: 'inherit'}} >
      <Stack direction="row" spacing={1} sx={{ }}>
          <Box sx={{marginRight: -0.5}}>
              <LinkIcon size={20} color={textColor} />
          </Box>
          <Typography variant='body2' sx={{  }} color={textColor}>
            {linkName}
          </Typography>
      </Stack>
      </Link>
    : 
      <Typography variant='body2' sx={{  }} color={textColor}>
        {linkName}
      </Typography> 
  );
}

export function CurValTable({config={}, shouldOverrideTitle, selectable=false, onDeselectAll, onDeselectOne, onSelectAll, onSelectOne, selected=[]}) {
  const logger = useComponentLogger("Table");
  const { currentProject, currentView, selectedViewParams, changedParams } = useContext(DataContext);
  const { cardTitle, setCardTitle } = useContext(CardContext);
  const [tableRows, setTableRows] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [title, setTitle] = useState(config?.title || '');
  const trackedParamValsRef = useRef({});
  const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr
  const [refreshInterval, setRefreshInterval] = useState(0);

  logger.debug('table object', config, selectable);

    // fetch and set content
    const fetchContent = async () => {
      logger.debug('fetch called', selectedViewParams);
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;
        
        if (dataSource.type === "text") {
          logger.debug('table data', config.dataSource.text);
          parseTableData(config.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('table data', data, dataSource.expr);
            parseTableData(data);
        }

    }

    const parseTableData = (data) => {
      // set card title
      if (data.meta && data.meta.title && shouldOverrideTitle) setCardTitle(data.meta.title);

      // rows is an array of 1 obj, with a key per data point
      // cols is an array of objects, 1 object epr data point
      if (!data?.rows || data.rows.length === 0 || !data?.cols || data.cols.length === 0) return;
      let displayMeta = data.meta?.presentation?.rows || [];

      // prune display meta
      let rowDisplay = displayMeta?.filter((display) => {
        if (!display.hasOwnProperty('col')) return false; // we currently only take col specs, modify this in the future if that changes
        if (display.row && display.row < 0) return false; // safeguard against -ve rows
        return true;
      });
      
      let formattedCols = [];
      let formattedRows = [];

      // build rows
      let iconColumns = [];
      let statusColumns = [];
      let linkColumns = [];

    logger.debug('row display', rowDisplay);
    // logger.debug('table rows', data.rows);

      for (const index in data.rows) {
        // logger.debug('cur row', index, data.rows[index], data.meta);
        let row = data.rows[index];
        for (const display of rowDisplay) {
          // Add to arrays to determine formatter function
          if (display?.mobileLinkName || display?.mobileLinkValue) {
            if (!linkColumns.includes(display.col)) linkColumns.push(display.col);
          } else if (display?.mobileIcon) {
            if (!iconColumns.includes(display.col)) iconColumns.push(display.col);
          } else if (display?.background) {
            if (!statusColumns.includes(display.col)) statusColumns.push(display.col);
          }
        }
        

        // TODO: reintroduce filter?
        // cells can either be a link, icon or status but not all 3
        // if a cell has both an icon and background, use the icon formatter
        iconColumns = iconColumns.filter(item => !linkColumns.includes(item));
        statusColumns = statusColumns.filter(item => !iconColumns.includes(item) && !linkColumns.includes(item));
        // logger.debug("cols before filter", linkColumns, iconColumns, statusColumns)
        
        let rowConfig = {};

        for (const key in row) {
            let item = row[key];
            // logger.debug('cur row item', item);
            if (typeof(item) === 'object' && item !== null) {
                if (item.val === null || item.val === undefined) continue;
                rowConfig[key] = {
                  value: item.val,
                  unit: item.unit || null,
                  dis: item.dis || null
                };
            } else { // string
                rowConfig[key] = {value: item};
            }

        }

        
        formattedRows.push(rowConfig);
      }

    // logger.debug('formatted rows', formattedRows);
    // logger.debug('icon, status cols', iconColumns, statusColumns);

      for (const display of rowDisplay) {
      logger.debug('cur display', display, formattedRows[display.row][display.col]);
        // Add to arrays to determine formatter function
        if (display?.mobileLinkName || display?.mobileLinkValue) {
          if (!linkColumns.includes(display.col)) linkColumns.push(display.col);
        } else if (display?.mobileIcon) {
          if (!iconColumns.includes(display.col)) iconColumns.push(display.col);
        } else if (display?.background) {
          if (!statusColumns.includes(display.col)) statusColumns.push(display.col);
        }
        
        // in case a cell is empty?
        if (formattedRows[display.row][display.col]) {
          if (display?.background) formattedRows[display.row][display.col]['color'] = display.background;
          if (display?.mobileIcon) formattedRows[display.row][display.col]['icon'] = display.mobileIcon
          if (display?.mobileLinkName ) formattedRows[display.row][display.col]['linkName'] = display.mobileLinkName
          if (display?.mobileLinkValue ) formattedRows[display.row][display.col]['linkValue'] = display.mobileLinkValue
        }
      }

      

      
      setTableRows(formattedRows);

      // build columns
      for (const col of data.cols) {
        logger.debug('cur col', col, statusColumns, iconColumns, linkColumns);
        if (!col.name) continue;
        let colConfig = { field: col.name, name: col.meta?.dis || col.meta?.navName || col.name || ''};
        if (linkColumns.includes(col.name)) {
          colConfig.formatter = linkFormatter;
        } else if (statusColumns.includes(col.name)) {
          colConfig.formatter = statusFormatter;
        } else {
          colConfig.formatter = iconFormatter;
        }
        formattedCols.push(colConfig);     
      }

      // diff styling for the first column
      if (formattedCols.length > 0) formattedCols[0].formatter = primaryFormatter;
      setTableColumns(formattedCols);

      logger.debug('parsed rows', formattedRows, formattedCols, iconColumns, statusColumns, linkColumns);
    }
    
   
    useEffect(() => {
      logger.debug('table config', config)
      let trackedParams = extractTrackedParams(config.dataSource);
      let params = {};
      for (const param of trackedParams) {
          if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
      }

      logger.debug('tracked params', params);
      trackedParamValsRef.current = params;
      trackedParamNamesRef.current = trackedParams;
      logger.debug('fetch called by []')
      fetchContent().catch(() => {});
      setRefreshInterval(60000);
    }, []);

    useEffect(() => {
      let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
      logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
      if (shouldReFetch.changed) {
          trackedParamValsRef.current = shouldReFetch.paramRef;
          logger.debug('fetch called via changedParams', shouldReFetch.changed)
          fetchContent();
      }

      // make sure to clear the content if any of the tracked params were removed
      // common use case: when a parent param changes, it clears the child param
      for (const param of trackedParamNamesRef.current) { 
          if (!selectedViewParams[param]) {
            setTableRows([]);
            setTableColumns([]);
            return;
          }
      }
    }, [changedParams]);

    useEffect(() => {
      logger.debug('fetch called via config')
      fetchContent();
    }, [config]);

    // Start the interval only once we have a refreshInterval > 0
    useInterval(fetchContent, refreshInterval || null);

    return (
        <>
        {config?.title ? 
            <Typography color="text.secondary" variant="h6" margin={2} sx={{display: 'flex', alignItems: 'center'}}>
                {config.title}
            </Typography>
        : <></>}
        <Box width='100%'  sx={{ overflowX: 'auto', marginTop: 0 }}>
            <DataTable 
              columns={tableColumns} 
              rows={tableRows} 
              selectable={selectable} 
              onDeselectAll={onDeselectAll} 
              onDeselectOne={(_, row) => {
                onDeselectOne(row.id);
              }}
              onSelectAll={onSelectAll} 
              onSelectOne={(_, row) => {
                onSelectOne(row.id);
              }} 
              selected={selected}
            />
        </Box>
        </>
    );
}
